import React, { useState } from "react";
import { Calculator, Loader2 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput, SelectInput, SliderInput } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import { useProducts } from "@/hooks/useProducts";
import { postTool } from "@/lib/api";
import { formatUSD, buildApplyQuery } from "@/lib/format";

const STATES = [
  { value: "national-average", label: "National Average" },
  { value: "CA", label: "California" },
  { value: "NY", label: "New York" },
  { value: "MA", label: "Massachusetts" },
  { value: "CT", label: "Connecticut" },
  { value: "NJ", label: "New Jersey" },
  { value: "TX", label: "Texas" },
  { value: "FL", label: "Florida" },
  { value: "IL", label: "Illinois" },
  { value: "OH", label: "Ohio" },
  { value: "PA", label: "Pennsylvania" },
  { value: "GA", label: "Georgia" },
  { value: "NC", label: "North Carolina" },
  { value: "AZ", label: "Arizona" },
  { value: "WA", label: "Washington" },
  { value: "OR", label: "Oregon" },
  { value: "CO", label: "Colorado" },
  { value: "MI", label: "Michigan" },
  { value: "VA", label: "Virginia" },
  { value: "MN", label: "Minnesota" },
  { value: "WI", label: "Wisconsin" },
];

const CARE_TYPES = [
  { value: "home-care", label: "Home Care" },
  { value: "assisted-living", label: "Assisted Living" },
  { value: "nursing-semi", label: "Nursing Home (Semi-private)" },
  { value: "nursing-private", label: "Nursing Home (Private room)" },
];

export default function LTCCost() {
  const { products } = useProducts();
  const [form, setForm] = useState({
    current_age: 60,
    state: "national-average",
    care_type: "assisted-living",
    years_of_care: 3,
    inflation_rate: 0.04,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.current_age < 18 || form.current_age > 100)
      e.current_age = "Age must be 18–100.";
    if (form.years_of_care < 1 || form.years_of_care > 15)
      e.years_of_care = "Use 1–15 years.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("ltc-cost", form);
      setResult(data);
    } catch {
      setServerError("Could not calculate. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CalculatorShell
      title="Long-Term Care Cost Estimator"
      blurb="Project the cost of home, assisted-living, or nursing-home care at age 80."
      duration="Takes ~45 seconds"
      Icon={Calculator}
    >
      <form
        onSubmit={onSubmit}
        data-testid="ltc-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumberInput
            label="Current age"
            value={form.current_age}
            onChange={set("current_age")}
            min={18}
            max={100}
            testId="ltc-age"
            error={errors.current_age}
          />
          <SelectInput
            label="State"
            value={form.state}
            onChange={set("state")}
            options={STATES}
            testId="ltc-state"
          />
          <SelectInput
            label="Care type"
            value={form.care_type}
            onChange={set("care_type")}
            options={CARE_TYPES}
            testId="ltc-care-type"
          />
          <NumberInput
            label="Expected years of care"
            value={form.years_of_care}
            onChange={set("years_of_care")}
            min={1}
            max={15}
            testId="ltc-years"
            helper="Industry average: ~3 years."
            error={errors.years_of_care}
          />
        </div>

        <SliderInput
          label="Care-cost inflation"
          value={form.inflation_rate}
          onChange={set("inflation_rate")}
          min={0}
          max={0.08}
          step={0.005}
          formatValue={(v) => `${(v * 100).toFixed(1)}%`}
          testId="ltc-inflation"
        />

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="ltc-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Estimating…
            </>
          ) : (
            <>Estimate care costs →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="ltc-cost"
          headline={`~${formatUSD(result.total_cost)} estimated total at age 80`}
          subhead={`${result.years_of_care} year(s) of ${result.care_type_label} at the inflated rate.`}
          rows={[
            {
              label: "Annual cost today",
              value: formatUSD(result.annual_cost_today),
              testId: "annual-today",
            },
            {
              label: "Annual cost at age 80",
              value: formatUSD(result.annual_cost_at_80),
              testId: "annual-80",
            },
            {
              label: "Years until age 80",
              value: `${result.years_until_80} yrs`,
              testId: "years-80",
            },
            {
              label: "Years of care",
              value: `${result.years_of_care} yrs`,
              testId: "years-care",
            },
          ]}
          assumptions={result.assumptions}
          matchedIds={result.matched_product_ids}
          products={products}
          applyQuery={buildApplyQuery({
            source: "ltc-cost",
            care_type: result.care_type,
            total: result.total_cost,
            current_age: form.current_age,
            products: (result.matched_product_ids || []).join(","),
          })}
          toolSlug="ltc-cost"
          toolInputs={form}
          toolResults={result}
        />
      )}
    </CalculatorShell>
  );
}
